import React, { useContext, useEffect } from 'react';
import ENDPOINT from '../../../../constants/endpoints';
import AsyncSelectPane from '../../../../GbsUI/components/AsyncSelectPane';
import Checkbox from '../../../../GbsUI/UI/Checkbox';
import Select from '../../../../GbsUI/UI/Select';
import TextInput from '../../../../GbsUI/UI/TextInput';
import { MONTHS_OPTIONS, YEAR_OPTIONS } from '../../constants';
import { SignupContext } from '../../context';

const EducationItem = ({
  education,
  index,
  degrees,
  gpa,
  isDegreesLoading,
  isGpaLoading,
  errors
}) => {
  const {state, setState} = useContext(SignupContext);

  useEffect(() => {
    if (index > 0 && (!education.major || !education.gpa)) {
      const naGpa = gpa.find((item) => item.label === 'NA');

      setState((current) => ({
        ...current,
        education: current.education.map((item, i) => i === index ? {
          ...item,
          ...(!education.major ? {
              major: {
                name: 'NA',
                id: '',
              }} : {}
          ),
          ...(!education.gpa ? {
            gpa: naGpa
          } : {})
        } : item),
      }));
    }
  }, [education.gpa, education.major, gpa, index, setState]);

  return (
    <div className={'signup-form-row'}>
      {index > 0 && (
        <div className={'signup-form-item signup-form-item--full'}>
          <Select
            name={`[${index}].degree`}
            errors={errors.get(`[${index}].degree`)}
            isLoading={isDegreesLoading}
            label={'Degree'}
            options={degrees}
            value={education.degree}
            onChange={v => {
              setState({
                ...state,
                education: state.education.map((item, i) => i === index ? {
                  ...item,
                  degree: v,
                } : item),
              });
            }}
          />
        </div>
      )}
      <div className={'signup-form-item signup-form-item--full'}>
        <AsyncSelectPane
          name={`[${index}].school`}
          errors={errors.get(`[${index}].school`)}
          label={index > 0 ? 'School' : 'Undergraduate School'}
          title={'Change your school'}
          selected={education.school}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                school: v,
              } : item),
            });
          }}
          source={ENDPOINT.TAXONOMIES.SCHOOLS}
          createNewTitle={'Can\'t find your school in the list?'}
          createNewSubtitle={'Select your University NOT your business school or program (select UPenn, not Wharton).'}
          createNewHelp={'If your University or College is not listed, you can manually add it above and press ENTER.'}
          createNewPlaceholder={'e.g. University of Virginia'}
          notFoundText={'School is not found'}
          searchPlaceholder={'Enter the school name'}
          help={'Select school where you completed or expect to complete Undergrad'}
          disclaimer={'Note: GoBuyside has no affiliation with these colleges and universities; names and logos are displayed for informational purposes and clarity.'}
          creatable
          autoselect={true}
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <Checkbox
          name={`[${index}].present`}
          className={'student-form__firm-checkbox'}
          onChange={(e) => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                present: e.target.checked,
              } : item),
            });
          }}
          label={'Check this box if you are currently enrolled'}
          checked={!!education.present}
        />
      </div>
      <div className={'signup-form-item'}>
        <Select
          name={`[${index}].startMonth`}
          errors={errors.get(`[${index}].startMonth`)}
          label={'Start Month'}
          options={MONTHS_OPTIONS}
          value={education.startMonth}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                startMonth: v,
              } : item),
            });
          }}
        />
      </div>
      <div className={'signup-form-item'}>
        <Select
          name={`[${index}].startYear`}
          errors={errors.get(`[${index}].startYear`)}
          label={'Start Year'}
          options={YEAR_OPTIONS}
          value={education.startYear}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                startYear: v,
              } : item),
            });
          }}
        />
      </div>
      <div className={'signup-form-item'}>
        <Select
          name={`[${index}].endMonth`}
          errors={errors.get(`[${index}].endMonth`)}
          label={`${education.present ? 'Expected ' : ''}Graduation Month`}
          options={MONTHS_OPTIONS}
          value={education.endMonth}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                endMonth: v,
              } : item),
            });
          }}
        />
      </div>
      <div className={'signup-form-item'}>
        <Select
          name={`[${index}].endYear`}
          errors={errors.get(`[${index}].endYear`)}
          label={`${education.present ? 'Expected ' : ''}Graduation Year`}
          options={YEAR_OPTIONS}
          value={education.endYear}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                endYear: v,
              } : item),
            });
          }}
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <AsyncSelectPane
          name={`[${index}].major`}
          errors={errors.get(`[${index}].major`)}
          label={'Major'}
          title={'Change your current major'}
          help={'If you had multiple majors or your major is not listed here, please choose the best option'}
          selected={education.major}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                major: v,
              } : item),
            });
          }}
          source={index === 0 ? ENDPOINT.TAXONOMIES.MAJORS : ENDPOINT.TAXONOMIES.MAJORS_WITH_NA}
          notFoundText={'Not found'}
          searchPlaceholder={'Enter the major'}
          showIcon={false}
          columns={4}
          autoselect
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <Select
          name={`[${index}].gpa`}
          errors={errors.get(`[${index}].gpa`)}
          label={'Cumulative GPA (4.0 Scale)'}
          help={'If your GPA is not based on a traditional 4.0 scale, please convert it to the most reasonable 4.0 equivalent and check the box below'}
          options={gpa}
          value={education.gpa}
          isLoading={isGpaLoading}
          onChange={v => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                gpa: v,
              } : item),
            });
          }}
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <Checkbox
          name={`[${index}].gpaNot4`}
          className={'student-form__firm-checkbox'}
          help={!!education.gpaNot4 ? 'If not based on a 4.0 scale, please convert your GPA to the most reasonable equivalent above' : ''}
          onChange={(e) => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                gpaNot4: e.target.checked,
              } : item),
            });
          }}
          label={'Click here if your University did not use a 4.0 scale'}
          checked={!!education.gpaNot4}
        />
      </div>
      <div className={'signup-form-item signup-form-item--full'}>
        <TextInput
          name={`[${index}].gpaNotes`}
          label={'GPA notes (optional)'}
          help={'Non-4.0 scale clarification, GPA in major, extenuating circumstances or other notes.'}
          value={education.gpaNotes || ''}
          rows={6}
          onChange={e => {
            setState({
              ...state,
              education: state.education.map((item, i) => i === index ? {
                ...item,
                gpaNotes: e.target.value,
              } : item),
            });
          }}
        />
      </div>
      <p className={'gpa-description'}>
        Integrity is a core value at GoBuyside. We reserve the right to verify GPA submissions.<br />
        Falsified information will result in a lifetime platform ban.
      </p>
    </div>
  )
};

export default EducationItem;
